import { useMutation } from '@tanstack/react-query';
import UserService from '~/modules/services/user-service';
import { useContext } from 'react';
import { SessionContext } from '~/modules/contexts';
import AuthService from '~/modules/services/auth-service';
import { setupAPI } from '~/modules/services/api-service';
import RouteService from '~/modules/services/route-service';
import StorageService from '~/modules/services/storage-service';
import { handleError } from '~/utils/handle-error';
import { completeDocsHandshake, DOCS_AUTH_PATH } from '~/utils/docs-handshake';
import SessionService from '~/modules/services/session-service';
import AsperaService from '~/modules/services/aspera-service';

export default function useAuth() {
	let {
		auth, setAuth
	} = useContext(SessionContext);

	const redirectAfterLogin = (session) => {
		if( session?.guestAccessShare ) {
			StorageService.remove('lastRoute');
			RouteService.navigateTo('share-view', { id: session.guestAccessShare });
			return;
		}

		// A docs SSO visitor signs in on `/docs/auth?redirect=…` (the login modal overlays the
		// route whose auth gate just failed, so its minting page never mounted). Now that we're
		// authenticated, complete the handshake straight to the Papervine callback instead of
		// falling through to the SPA. The destination rides in the URL, which the handshake reads
		// itself — `lastRoute` is cookie-backed and easily stale, so we don't rely on it.
		if( window.location.pathname === DOCS_AUTH_PATH ) {
			StorageService.remove('lastRoute');
			completeDocsHandshake();
			return;
		}

		const redirectUrl = StorageService.get('lastRoute');
		if( redirectUrl && !redirectUrl.includes('/login') && !redirectUrl.includes('/register') && !redirectUrl.includes('/guest') ) {
			StorageService.remove('lastRoute');
			window.location.href = redirectUrl;
		}
		else {
			RouteService.navigateTo('projects');
		}
	};

	const handleAuthenticatedSessionSuccess = (session, options = {}) => {
		const { redirect = true } = options;
		setupAPI(session, true);
		setAuth({ session, isLoggedIn: true, lastAttemptedAt: new Date() });
		if( redirect ) {
			redirectAfterLogin(session);
		}
	};

	/*
		Login mutation
	*/
	const {
		mutateAsync: login,
		isLoading: isLoggingIn
	} = useMutation({
		mutationKey: ['session'],
		mutationFn: AuthService.login,
		onMutate: () => {
			// `ssoLoginError` is cleared here too: a refused Google or Microsoft sign-in
			// would otherwise keep its banner in the modal through every later password
			// attempt, rendering alongside "Invalid username or password". One slot for both
			// providers, because only the most recent attempt is worth reporting.
			setAuth({ ...auth, lastAttemptedAt: new Date(), loginError: null, ssoLoginError: null });
		},
		onSuccess: (session) => {
			if( session.twofactor ) {
				// Not a session yet. `login` resolves with the challenge and the auth modal keeps
				// it in its own state for the code step. This used to copy it into
				// `SessionContext` as well -- `isTwoFactorLogin`, `twoFactorMethod`,
				// `twoFactorToken`, `twoFactorRecoveryCodes` -- where nothing read it any more:
				// two copies of one half-finished sign-in, and the global one lived on, stale,
				// for the rest of the page.

				// Redirect to login page if current location is register page
				if( window.location.pathname.includes('/register') ) {
					window.location.replace('/login');
				}

				return;
			}

			// Nowhere to send somebody who owes us a passkey: the enrollment screen is what
			// comes next, and a redirect would take them off it -- which is exactly what it did,
			// flashing past at the end of the invitation flow.
			handleAuthenticatedSessionSuccess(session, { redirect: !session?.passkeyRequired });
		},
		onError: (e) => {
			// A failure `AuthService.login` marked `surfaced` already carries a sentence the
			// caller shows inline. `handleError` only stays quiet for 401–422, so a 503 from the
			// password path toasted and filed a Sentry report on top of the message on the form.
			if( e?.surfaced ) {
				setAuth({ ...auth, loginError: e });

				return;
			}

			const err = handleError(e.status === 403
				? {
					...e,
					message: 'Invalid username or password'
				}
				: e);
			setAuth({ ...auth, loginError: err });
		}
	});

	const {
		mutateAsync: googleLogin,
		isLoading: isGoogleLoggingIn
	} = useMutation({
		mutationKey: ['googleSession'],
		mutationFn: AuthService.googleLogin,
		onMutate: () => {
			// Both errors clear, in both directions: whichever method is being tried now owns
			// the feedback, so a stale "Invalid username or password" does not sit next to a
			// Google refusal (or the other way round).
			setAuth({ ...auth, lastAttemptedAt: new Date(), loginError: null, ssoLoginError: null });
		},
		onSuccess: (session) => {
			handleAuthenticatedSessionSuccess(session);
		},
		onError: (e) => {
			// The API's own wording is the useful part here — an address with no Pixwel
			// account, or an account that is no longer active, is something the person can
			// act on, unlike "invalid username or password".
			setAuth({ ...auth, ssoLoginError: e });
		}
	});

	const {
		mutateAsync: microsoftLogin,
		isLoading: isMicrosoftLoggingIn
	} = useMutation({
		mutationKey: ['microsoftSession'],
		mutationFn: AuthService.microsoftLogin,
		onMutate: () => {
			setAuth({ ...auth, lastAttemptedAt: new Date(), loginError: null, ssoLoginError: null });
		},
		onSuccess: (session) => {
			handleAuthenticatedSessionSuccess(session);
		},
		onError: (e) => {
			setAuth({ ...auth, ssoLoginError: e });
		}
	});

	const {
		mutateAsync: passkeyLogin,
		isLoading: isPasskeyLoggingIn
	} = useMutation({
		mutationKey: ['passkeySession'],
		mutationFn: AuthService.passkeyLogin,
		onMutate: () => {
			setAuth({ ...auth, lastAttemptedAt: new Date(), loginError: null, ssoLoginError: null });
		},
		onSuccess: (session) => {
			handleAuthenticatedSessionSuccess(session);
		},
		onError: (e) => {
			// Shares the SSO error slot: it is the same kind of failure from the person's point
			// of view -- a credential the API would not accept -- and only the most recent
			// attempt is worth reporting.
			setAuth({ ...auth, ssoLoginError: e });
		}
	});

	const {
		mutateAsync: guestLogin,
		isLoading: isGuestLoggingIn
	} = useMutation({
		mutationKey: ['guestSession'],
		mutationFn: AuthService.guestLogin,
		onMutate: () => {
			setAuth({ ...auth, lastAttemptedAt: new Date(), loginError: null, ssoLoginError: null });
		},
		onSuccess: (session, variables) => {
			handleAuthenticatedSessionSuccess(session, {
				redirect: variables?.redirect !== false
			});
		},
		onError: (e) => {
			setAuth({ ...auth, loginError: handleError(e) });
		}
	});

	/* Reset Password mutation */
	const {
		mutate: resetPassword,
		isLoading: isResettingPassword
	} = useMutation({
		mutationKey: ['resetPassword'],
		mutationFn: AuthService.resetPassword,
		onSuccess: () => {
			setAuth({ ...auth, resetPasswordSuccess: true, resetPasswordError: null });
		},
		onError: (e) => {
			setAuth({ ...auth, resetPasswordSuccess: false, resetPasswordError: handleError(e) });
		}
	});

	/*
		request Reset password mutation
	*/
	const {
		mutate: requestResetPassword
	} = useMutation({
		mutationKey: ['resetPassword'],
		mutationFn: AuthService.requestResetPassword,
		onSuccess: () => {
			setAuth({ ...auth, resetPasswordSuccess: true, resetPasswordError: null });
		},
		onError: (e) => {
			setAuth({ ...auth, resetPasswordSuccess: false, resetPasswordError: handleError(e) });
		}
	});

	/*
		Reset password feedback
	 */
	const clearResetPasswordFeedback = () => {
		// Called when the modal returns to the login form, which is the other moment a stale
		// Google refusal should stop being shown.
		setAuth({ ...auth, resetPasswordSuccess: false, resetPasswordError: null, ssoLoginError: null });
	};

	/**
	 * Forget the last refusal, whichever way in produced it.
	 *
	 * The passkey and provider sign-ins report through `ssoLoginError`, which outlives the
	 * modal's own step state -- so without this, changing the address or reopening the modal
	 * kept showing the previous account's refusal on the new address's screens.
	 */
	const clearLoginErrors = () => {
		setAuth({ ...auth, loginError: null, ssoLoginError: null });
	};

	/*
		Two factor login
	 */
	const {
		mutate: twoFactorLogin,
		isLoading: isTwoFactorLoggingIn
	} = useMutation({
		mutationKey: ['twoFactorLogin'],
		// A bare token still works: the emailed-link page passes one, and for anybody without an
		// authenticator that is the whole credential.
		mutationFn: (input) => AuthService.twoFactorLogin(input),
		onMutate: () => {
			setAuth({ ...auth, twoFactorLoginError: null });
		},
		onSuccess: ({ data }) => {
			setAuth({ ...auth, twoFactorLoginSuccess: true });
			// Fire-and-forget: login's own onError surfaces a failure, so swallow the
			// mutateAsync rejection to avoid a duplicate unhandled-rejection report.
			login({ username: data?.emailNormal || data?.email || '', token: data?.token ?? '', isTwoFactor: true })
				.catch(() => { /* handled by onError */ });
		},
		onError: (e) => {
			const error = handleError(e);
			setAuth({ ...auth, twoFactorLoginSuccess: false, twoFactorLoginError: error?.message || 'An error occurred' });
		}
	});

	/*
		Resume session
	 */

	const { mutateAsync: resumeSession } = useMutation(
		['session'],
		() => AuthService.resumeSession(),
		{
			onSuccess: (session) => {
				setupAPI(session);
				const token = AuthService.getToken();
				AsperaService.setCredentials(session, token);
				setAuth({
					...auth,
					session,
					isLoggedIn: true,
					lastAttemptedAt: new Date(),
					lastUserEmail: session.emailNormal
				});
			},
			onError: () => {
				setAuth({ ...auth, session: null, loginError: null, lastAttemptedAt: new Date() });
			}
		}
	);

	/*
		Get session
	*/

	const { mutateAsync: getSession } = useMutation(
		['getSession'],
		() => SessionService.getSession(),
		{
			onSuccess: (session) => {
				setupAPI(session);
				setAuth({ ...auth, session, isLoggedIn: true, isLoading: true, lastLoadedAt: new Date() });
			},
			onError: () => {
				setAuth({ ...auth, session: null, isLoading: false });
			}
		}
	);

	/*
	logout mutation
	*/
	const { mutate: logout } = useMutation({
		mutationKey: ['logout'],
		mutationFn: AuthService.logout
	});

	/*
		Update user mutation
	*/

	const { mutateAsync: updateUser, isLoading: isUserSaving } = useMutation(
		(data) => UserService.updateUser(auth.session.id, data),
		{
			// The name is on the avatar menu and beside every comment, so the session the app is
			// holding has to learn about it. Nothing refetches it until a reload, and the panel
			// that writes it closes on save -- so without this somebody renames themselves and
			// goes on seeing the old name all day.
			onSuccess: (result, data) => {
				setAuth({
					...auth,
					session: {
						...auth.session,
						...(data?.name ? { name: data.name } : {}),
						...(data?.territory ? { territory: data.territory } : {})
					}
				});
			}
		}
	);

	function isLoaded() {
		return !!auth.lastLoadedAt;
	}
	return {
		...auth,
		resumeSession,
		getSession,
		login,
		logout,
		twoFactorLogin,
		isTwoFactorLoggingIn,
		isUserSaving,
		updateUser,
		isLoggingIn,
		googleLogin,
		isGoogleLoggingIn,
		microsoftLogin,
		isMicrosoftLoggingIn,
		passkeyLogin,
		isPasskeyLoggingIn,
		guestLogin,
		isGuestLoggingIn,
		guestLoginError: auth.loginError,
		isLoaded,
		isResettingPassword,
		requestResetPassword,
		resetPassword,
		clearResetPasswordFeedback,
		clearLoginErrors

	};
}
